import { useState } from "react";
import { toast } from "sonner";
import { Folder, FolderOpen, FolderPlus, Inbox, Library, Pencil, Trash2, Check, X } from "lucide-react";
import { api, errText } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { TagChip } from "@/components/library/TagChip";

function FolderRow({ f, active, onSelect, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(f.name);
  const save = async () => {
    if (!name.trim() || name.trim() === f.name) return setEditing(false);
    try { await api.patch(`/folders/${f.id}`, { name }); setEditing(false); onChanged(); }
    catch (err) { toast.error(errText(err, "Couldn't rename")); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete folder "${f.name}"? Its documents stay in your library, just unfiled.`)) return;
    try { await api.delete(`/folders/${f.id}`); onChanged(f.id); }
    catch (err) { toast.error(errText(err, "Couldn't delete")); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1 px-2 py-1">
        <Input autoFocus data-testid={`folder-rename-input-${f.id}`} value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} className="h-7 rounded-sm text-sm" />
        <button data-testid={`folder-rename-save-${f.id}`} onClick={save} className="p-1 hover:text-primary"><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => setEditing(false)} className="p-1 hover:text-primary"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <div className={`group flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer ${active ? "bg-foreground text-background" : "hover:bg-muted"}`} onClick={() => onSelect(f.id)} data-testid={`folder-item-${f.id}`}>
      {active ? <FolderOpen className="w-4 h-4 shrink-0" /> : <Folder className="w-4 h-4 shrink-0 text-muted-foreground" />}
      <span className="truncate flex-1">{f.name}</span>
      <span className={`text-xs ${active ? "opacity-70" : "text-muted-foreground"} group-hover:hidden`}>{f.count}</span>
      <span className="hidden group-hover:flex items-center gap-0.5">
        <button data-testid={`folder-rename-${f.id}`} onClick={(e) => { e.stopPropagation(); setName(f.name); setEditing(true); }} className="p-0.5 hover:text-primary" aria-label="Rename folder"><Pencil className="w-3 h-3" /></button>
        <button data-testid={`folder-delete-${f.id}`} onClick={(e) => { e.stopPropagation(); remove(); }} className="p-0.5 hover:text-destructive" aria-label="Delete folder"><Trash2 className="w-3 h-3" /></button>
      </span>
    </div>
  );
}

export default function LibrarySidebar({ folders, docs, folderFilter, setFolderFilter, tagFilter, setTagFilter, onFoldersChanged }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const unfiled = docs.filter((d) => !d.folder_id).length;
  const tagCounts = docs.reduce((acc, d) => { (d.tags || []).forEach((t) => { acc[t] = (acc[t] || 0) + 1; }); return acc; }, {});
  const tags = Object.keys(tagCounts).sort((a, b) => a.localeCompare(b));

  const create = async () => {
    const name = newName.trim();
    if (!name) return setCreating(false);
    try {
      const { data } = await api.post("/folders", { name });
      setNewName(""); setCreating(false);
      await onFoldersChanged();
      setFolderFilter(data.id);
      toast.success(`Folder "${data.name}" created`);
    } catch (err) { toast.error(errText(err, "Couldn't create folder")); }
  };

  const navItem = (key, label, Icon, count, testId) => (
    <button data-testid={testId} onClick={() => setFolderFilter(key)}
      className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm ${folderFilter === key ? "bg-foreground text-background" : "hover:bg-muted"}`}>
      <Icon className={`w-4 h-4 shrink-0 ${folderFilter === key ? "" : "text-muted-foreground"}`} />
      <span className="flex-1 text-left">{label}</span>
      <span className={`text-xs ${folderFilter === key ? "opacity-70" : "text-muted-foreground"}`}>{count}</span>
    </button>
  );

  return (
    <aside data-testid="library-sidebar" className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-2 px-2">
          <span className="font-mono-label text-muted-foreground">Folders</span>
          <button data-testid="folder-new-btn" onClick={() => setCreating(true)} className="text-muted-foreground hover:text-foreground" aria-label="New folder"><FolderPlus className="w-4 h-4" /></button>
        </div>
        <div className="space-y-0.5">
          {navItem("all", "All documents", Library, docs.length, "folder-filter-all")}
          {navItem("none", "Unfiled", Inbox, unfiled, "folder-filter-none")}
          {folders.map((f) => (
            <FolderRow key={f.id} f={f} active={folderFilter === f.id} onSelect={setFolderFilter}
              onChanged={(deletedId) => { if (deletedId && folderFilter === deletedId) setFolderFilter("all"); onFoldersChanged(); }} />
          ))}
          {creating && (
            <div className="flex items-center gap-1 px-2 py-1">
              <Input autoFocus data-testid="folder-new-input" placeholder="Folder name" value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") create(); if (e.key === "Escape") { setCreating(false); setNewName(""); } }} className="h-7 rounded-sm text-sm" />
              <button data-testid="folder-new-save" onClick={create} className="p-1 hover:text-primary" aria-label="Create folder"><Check className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className="font-mono-label text-muted-foreground mb-2 px-2 flex items-center justify-between">
          <span>Tags</span>
          {tagFilter.length > 0 && <button data-testid="tag-filter-clear" onClick={() => setTagFilter([])} className="text-xs normal-case tracking-normal underline">clear</button>}
        </div>
        {tags.length === 0 ? (
          <p className="px-2 text-xs text-muted-foreground">No tags yet. Hover a document and pick “Tags” to add some.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 px-2">
            {tags.map((t) => (
              <TagChip key={t} name={t} count={tagCounts[t]} active={tagFilter.includes(t)} testId={`tag-filter-${t}`}
                onClick={() => setTagFilter(tagFilter.includes(t) ? tagFilter.filter((x) => x !== t) : [...tagFilter, t])} />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
